import { push } from 'connected-react-router';
import routeConstants from '@constants/routeConstants';
import errorCodeConstants from '@constants/errorCodeConstants';

import { triggerAnalyticsEvent, updateDeliveryOptionAnalytics } from '@redux/actions/analytics/analyticsAction';
import { recordPaymentTimings } from '@redux/actions/timing/timingActions';
import {
  ANALYTICS_DELIVERY_OPTIONS,
  ANALYTICS_SUBMIT_ORDER_ERROR,
  APPLE_PAY_SELECTED,
  GET_PAYMENT_PAGE,
  GOOGLE_PAY_DEFAULT,
  GOOGLE_PAY_ENABLED,
  INIT_PAYMENT_PAGE,
  SAVED_CARD_SELECTED,
  VOUCHER_UNDERSPEND_WARNING_MODAL_CLOSE,
  GET_ORDERFORM_DELIVERIES,
  APPLE_PAY_ENABLED,
  GET_ORDERFORM_DELIVERIES_NO_ACTION,
  GET_ORDERFORM_COLLECTION,
  INIT_ORDERFORM,
} from '@constants/actionConstants';
import {
  URL_PAYMENT_PAGE,
  URL_DELIVERIES_V3,
  URL_DELIVERIES_V4,
  URL_ORDERFORM_COLLECTION,
} from '@constants/endpointConstants';
import { canMakeGooglePayPayments } from '@redux/actions/payment/googlePayActions';
import { getItems, showBatchingFailureModal } from '@redux/actions/edit-basket/editBasketActions';
import { getDeliveryMethodsAnalytics } from '../delivery/deliveryActions';
import { selectClickAndCollectStoreAnalytics } from '@utils/analytics/utils/getCollectionAnalytics';
import handleInconsistentOrderFormState from '@utils/orderform/handleInconsistentOrderFormState';
import paymentTypeConstants from '@constants/paymentTypeConstants';
import featureConstants, { DISABLED_FEATURE } from '@constants/featureConstants';
import { isFeatureActive, isFeatureDisabled } from '@redux/reducers/config/configReducer';
import { isClickAndCollectChoice, isDeliveryChoice } from '@utils/delivery/deliveryHelper';
import { shouldTriggerApplyRewards } from '@utils/payment/shouldApplyRewards';
import { shouldSetPreferredDeliveryChoice } from '@redux/actions/fulfilment/shouldSetPreferredDeliveryChoice';
import { getIsExpressCheckoutSelector } from '@redux/reducers/app/appSelector';

import { applyRewards } from './rewardsActions';
import determineSelectedPaymentType from './selectedPaymentTypeActions';
import { getPayPalClientToken } from './payPalPaymentActions';

const dispatchPayOptionAnalytics =
  (customerCanMakeGooglePayPayments, applePayCompatibilityChecksComplete) => async (dispatch, getState) => {
    // `payment.selectedPaymentType` should be taken from actual state after success InitPaymentPage
    const selectedPaymentType = getState().payment?.selectedPaymentType;
    if (selectedPaymentType?.includes(paymentTypeConstants.SAVED_PAYMENT_CARD)) {
      dispatch(triggerAnalyticsEvent(SAVED_CARD_SELECTED));
    }

    if (applePayCompatibilityChecksComplete) {
      dispatch(triggerAnalyticsEvent(APPLE_PAY_ENABLED));

      const applePaySelected = selectedPaymentType?.includes(paymentTypeConstants.APPLE_PAY);
      if (applePaySelected) {
        dispatch(triggerAnalyticsEvent(APPLE_PAY_SELECTED));
      }
    }

    if (customerCanMakeGooglePayPayments) {
      dispatch(triggerAnalyticsEvent(GOOGLE_PAY_ENABLED));
      if (selectedPaymentType?.includes(paymentTypeConstants.GOOGLE_PAY)) {
        dispatch(triggerAnalyticsEvent(GOOGLE_PAY_DEFAULT));
      }
    }
  };

export const initPaymentPage =
  (params = {}) =>
  async (dispatch, getState) => {
    const { eventDetails, getOnly, pushAfter, silentlyPrefetchPaymentData } = params;

    const { app, clickAndCollect, orderForm, delivery, payment } = getState();

    if (payment?.getPaymentPageCallActive) {
      // try to stop mysterious duplicate calls to payment-page
      return {};
    }

    const selectedCollectionPoint = clickAndCollect?.selectedCollectionPoint;
    const deliveries = delivery?.deliveries ?? [];
    const confirmedDeliveryAddress = delivery?.confirmedDeliveryAddress;
    const selectedDeliveryChoiceId = delivery?.selectedDeliveryChoiceId;
    const orderNeedsToBeRebatched = orderForm?.orderNeedsToBeRebatched;

    const disableInitPaymentPage =
      orderNeedsToBeRebatched || (app?.orderComplete ?? false) || (app?.orderProcessing ?? false);

    if (disableInitPaymentPage) {
      return {};
    }

    const notReadyForPayment =
      (isDeliveryChoice(selectedDeliveryChoiceId) && !confirmedDeliveryAddress) ||
      (isClickAndCollectChoice(selectedDeliveryChoiceId) && !selectedCollectionPoint) ||
      !selectedDeliveryChoiceId;

    if (notReadyForPayment) {
      const pathname = isClickAndCollectChoice(selectedDeliveryChoiceId)
        ? routeConstants.CLICK_AND_COLLECT
        : routeConstants.DELIVERY;
      dispatch(push({ pathname }));
      return {};
    }

    if (isDeliveryChoice(selectedDeliveryChoiceId)) {
      dispatch(getDeliveryMethodsAnalytics(delivery?.deliveries));
    }

    if (isClickAndCollectChoice(selectedDeliveryChoiceId)) {
      const searchTerm = clickAndCollect?.collectionPointSearchTerm;
      const collectionDates = clickAndCollect?.collectionPointDates;
      const analytics = selectClickAndCollectStoreAnalytics(selectedCollectionPoint, searchTerm, collectionDates);

      dispatch(
        triggerAnalyticsEvent(ANALYTICS_DELIVERY_OPTIONS, {
          analytics,
        }),
      );
    }

    const customerCanMakeGooglePayPayments = getState().payment?.canMakeGooglePayPayments;
    if (customerCanMakeGooglePayPayments) {
      await dispatch(canMakeGooglePayPayments());
    }

    const paymentPageResponse = await dispatch({
      type: getOnly ? GET_PAYMENT_PAGE : INIT_PAYMENT_PAGE,
      request: (client) =>
        client({
          path: URL_PAYMENT_PAGE,
          config: { method: getOnly ? 'GET' : 'POST' },
        }),
      selectedCollectionPoint,
      deliveries,
      ...(silentlyPrefetchPaymentData && { silentlyPrefetchPaymentData }),
      ...(pushAfter && { pushAfter }),
      ...(eventDetails && { eventDetails }),
    });

    if (!paymentPageResponse?.type?.includes('FAILED')) {
      dispatch(
        getOrderFormData({
          isCustomerAction: true,
        }),
      );
    }

    if (paymentPageResponse.type === `${INIT_PAYMENT_PAGE}.SUCCESS`) {
      if (!silentlyPrefetchPaymentData) {
        dispatch(recordPaymentTimings());
      }

      await dispatch(determineSelectedPaymentType());

      const isDisabled =
        isFeatureDisabled(getState(), DISABLED_FEATURE.PAYPAL) ||
        isFeatureActive(getState(), featureConstants.DISABLE_PAYPAL);

      if (!isDisabled && payment && !payment.payPalClientToken) {
        const showError = false;
        dispatch(getPayPalClientToken(showError));
      }
    }

    const applePayCompatibilityChecksComplete = getState().payment?.applePayCompatibilityChecksComplete;

    if (!pushAfter) {
      dispatch(dispatchPayOptionAnalytics(customerCanMakeGooglePayPayments, applePayCompatibilityChecksComplete));
    }

    const error = paymentPageResponse?.error?.code ?? '';
    if (error === errorCodeConstants.ORDER_FORM_INCONSISTENT_STATE) {
      await dispatch(handleInconsistentOrderFormState());
      return {};
    }

    if (pushAfter) {
      dispatch(push(routeConstants.PAYMENT));
      dispatch(dispatchPayOptionAnalytics(customerCanMakeGooglePayPayments, applePayCompatibilityChecksComplete));
    }

    return paymentPageResponse;
  };

export const getPaymentPage = (eventDetails) => initPaymentPage({ getOnly: true, eventDetails });

export const getPaymentPageAfterSubmitOrderError = (submitOrderAction) => async (dispatch) => {
  const response = await dispatch(
    initPaymentPage({
      getOnly: true,
      eventDetails: {
        posCreditSubmitOrderError:
          submitOrderAction.selectedPaymentType?.includes(paymentTypeConstants.POS_CREDIT) || false,
      },
    }),
  );

  if (response.type === `${GET_PAYMENT_PAGE}.SUCCESS`) {
    dispatch(triggerAnalyticsEvent(ANALYTICS_SUBMIT_ORDER_ERROR, { error: submitOrderAction.error }));
  }
};

export const closeVoucherUnderspendWarningModal = () => ({
  type: VOUCHER_UNDERSPEND_WARNING_MODAL_CLOSE,
});

export const refreshItems = (lineItemTotalBeforePutAddress) => async (dispatch, getState) => {
  const newLineItemTotal = getState()?.orderForm?.lineItemTotal;
  const lineItemTotalHasChanged = newLineItemTotal !== lineItemTotalBeforePutAddress;

  // in the unlikely event a MyJL customer has no saved address,
  // passing isCustomerAction: true here will cause the rewards/apply call to be made
  // instead of items
  if (lineItemTotalHasChanged) return dispatch(getOrderFormData({ isCustomerAction: true }));

  return {};
};

export const getOrderFormData =
  ({ isAgeVerification = false, isCustomerAction = false } = {}) =>
  async (dispatch, getState) => {
    if (shouldTriggerApplyRewards(getState())) {
      const rewardsResponse = await dispatch(
        applyRewards({
          isCustomerAction,
          shouldFallbackToItems: false,
        }),
      );
      if (rewardsResponse?.type?.includes('SUCCESS')) return rewardsResponse;
    }

    const getItemsResponse = await dispatch(getItems({ isAgeVerification }));
    return getItemsResponse;
  };

export const getOrderFormCollection = () => (dispatch, getState) => {
  const isExpressCheckout = getIsExpressCheckoutSelector(getState());

  if (isExpressCheckout) {
    return;
  }

  return dispatch({
    type: GET_ORDERFORM_COLLECTION,
    request: (client) => client({ path: URL_ORDERFORM_COLLECTION, config: { method: 'POST' } }),
  });
};

export const getOrderFormDeliveries =
  ({ method = 'POST' } = {}) =>
  async (dispatch, getState) => {
    dispatch({ type: INIT_ORDERFORM });

    const apoEnabled = isFeatureActive(getState(), featureConstants.APO_ENABLED);
    const path = apoEnabled ? URL_DELIVERIES_V4 : URL_DELIVERIES_V3;

    // need to inspect the response for the preferredDeliveryChoice feature before
    // reducers can update from it, so use no_action here and dispatch again later
    const response = await dispatch({
      type: GET_ORDERFORM_DELIVERIES_NO_ACTION,
      request: (client) => client({ path, config: { method } }),
    });

    dispatch(updateDeliveryOptionAnalytics(response));

    const fulfilmentChoice = getState().user?.customerPreferences?.fulfilmentChoice;
    const preferredDeliveryChoice = dispatch(shouldSetPreferredDeliveryChoice(fulfilmentChoice));
    if (apoEnabled) dispatch(updateDeliveryOptionAnalytics(response));

    if (response?.type?.includes('FAILED') && response?.error?.code === errorCodeConstants.BATCHING_FAILURE) {
      await dispatch(showBatchingFailureModal());
    }

    return dispatch({
      ...response,
      ...(preferredDeliveryChoice && { preferredDeliveryChoice }),
      type: response.type
        ? response.type.replace(GET_ORDERFORM_DELIVERIES_NO_ACTION, GET_ORDERFORM_DELIVERIES)
        : `${GET_ORDERFORM_DELIVERIES}.SUCCESS`,
      method,
    });
  };
